import { defineMessages, useIntl } from 'react-intl';

import { getPaymentMethodIcon, type IconName } from '@sg/garnish';
import { CardType } from '@sg/graphql-schema';

import { type PaymentMethod } from '../../types';
import { fallbackToSavedPaymentMethod, removeEndingIn } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useStripePaymentMethod = (props: UseStripePaymentMethodProps) => {
  const { hasStripeFailedToLoad, paymentMethods, name, icon, isGooglePay } = props;

  const { formatMessage } = useIntl();

  // Show default/first saved payment method if valid and no payment method is selected from the SDK.
  const fallbackPaymentMethod = fallbackToSavedPaymentMethod(paymentMethods);
  const isValidFallback = fallbackPaymentMethod?.cardType !== CardType.Unsupported; // prettier-ignore

  const fallbackName = isValidFallback ? fallbackPaymentMethod?.description : undefined; // prettier-ignore
  const fallbackIcon = isValidFallback ? getPaymentMethodIcon(fallbackPaymentMethod?.cardType) : undefined; // prettier-ignore

  const missingName = hasStripeFailedToLoad
    ? formatMessage(messages.stripeFailedToLoad)
    : formatMessage(messages.selectPaymentMethod);

  const paymentMethod = removeEndingIn(name || fallbackName || missingName);
  const paymentMethodIcon = getPaymentMethodIconWithFallback(icon, fallbackIcon, isGooglePay);
  const hasNoPaymentMethod = !name && !fallbackName;

  return {
    paymentMethod,
    paymentMethodIcon,
    hasNoPaymentMethod,
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function getPaymentMethodIconWithFallback(
  icon: IconName | undefined,
  fallbackIcon: IconName | undefined,
  isGooglePay: boolean | undefined,
): IconName {
  if (isGooglePay) {
    return 'IconGooglePay';
  }

  if (icon && icon !== 'IconCreditCard') {
    return icon;
  }

  if (fallbackIcon) {
    return fallbackIcon;
  }

  return 'IconCreditCard';
}

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  selectPaymentMethod: {
    defaultMessage: 'Select payment method',
    description: 'Bag | Selected Payment Method | Select payment method',
  },
  stripeFailedToLoad: {
    defaultMessage: 'Something went wrong.',
    description: 'Bag | Selected Payment Method | Stripe failed to load',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UseStripePaymentMethodProps = Readonly<{
  hasStripeFailedToLoad: boolean;
  paymentMethods: readonly PaymentMethod[];
  name?: string;
  icon?: IconName;
  imageBase64?: string;
  isGooglePay?: boolean;
}>;
